import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { Menu, Plus } from "lucide-react";
import { toast } from "sonner";
import { BrynLogo } from "@/components/BrynLogo";
import { PaywallDialog } from "@/components/Paywall";
import { AccessChip } from "@/components/Access";
import { http, formatError } from "@/lib/api";
import { InstallCard } from "@/components/InstallCard";
import { Sidebar, Rail } from "@/components/Sidebar";
import { QuickActions } from "@/components/QuickActions";
import { SearchChats } from "@/components/SearchChats";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useActivityTracker } from "@/lib/activity";
import { useAppTheme } from "@/lib/theme";
import { logWarn } from "@/lib/log";
import { useSwipeMenu } from "@/lib/swipeMenu";

export const openQuickActions = () => window.dispatchEvent(new Event("open-quick-actions"));

function useChats() {
  const [chats, setChats] = useState([]);
  const refreshChats = useCallback(() => http.get("/chats").then((r) => setChats(r.data)).catch((e) => logWarn("Couldn't load chats", e)), []);
  useEffect(() => {
    refreshChats();
    const onFocus = () => { if (document.visibilityState === "visible") refreshChats(); };
    window.addEventListener("folders-changed", refreshChats);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("folders-changed", refreshChats);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refreshChats]);
  // While any answer is still being written, keep Recents fresh so its "ready" dot appears without a reload.
  const generating = chats.some((c) => c.generating);
  useEffect(() => {
    if (!generating) return undefined;
    const t = setInterval(refreshChats, 4000);
    return () => clearInterval(t);
  }, [generating, refreshChats]);
  return { chats, refreshChats };
}

function MobileBar({ onMenu, onNew }) {
  return (
    <div className="md:hidden h-[calc(3rem+env(safe-area-inset-top))] shrink-0 flex items-center px-2 border-b border-white/[0.06] pt-[env(safe-area-inset-top)]">
      <button onClick={onMenu} className="h-11 w-11 flex items-center justify-center" data-testid="sidebar-open-btn" aria-label="Open menu"><Menu className="h-5 w-5" /></button>
      <span className="ml-1 flex-1 min-w-0"><BrynLogo markClass="h-7 w-7" /></span>
      <AccessChip className="mr-1" />
      <button onClick={onNew} className="h-11 w-11 flex items-center justify-center text-sky-300" data-testid="mobile-new-btn" aria-label="New"><Plus className="h-5 w-5" /></button>
    </div>
  );
}

export function AppShell() {
  const { user, logout, ent } = useAuth();
  const { chats, refreshChats } = useChats();
  const { open: drawer, setOpen: setDrawer, contentRef, overlayRef } = useSwipeMenu("(max-width: 767px)");
  const [quick, setQuick] = useState(false);
  const [search, setSearch] = useState(false);
  const [collapsed, setCollapsedState] = useState(() => localStorage.getItem("brynai.sidebarCollapsed") === "1");
  const setCollapsed = (v) => { setCollapsedState(v); localStorage.setItem("brynai.sidebarCollapsed", v ? "1" : "0"); };
  const navigate = useNavigate();
  const { chatId } = useParams();
  useActivityTracker(!!user);
  useAppTheme(user?.theme);
  useEffect(() => {
    const h = () => setQuick(true);
    window.addEventListener("open-quick-actions", h);
    return () => window.removeEventListener("open-quick-actions", h);
  }, []);
  const outletCtx = useMemo(() => ({ refreshChats, chats }), [refreshChats, chats]);

  const del = async (id) => {
    try { await http.delete(`/chats/${id}`); toast.success("Chat deleted"); } catch (e) { toast.error(formatError(e)); }
    refreshChats();
    if (id === chatId) navigate("/app");
  };
  const common = { user, ent, onLogout: logout, onNew: () => { setDrawer(false); setQuick(true); }, onSearch: () => { setDrawer(false); setSearch(true); } };
  const full = { ...common, chats, onDelete: del, onChanged: refreshChats };

  return (
    <div className="h-[100dvh] flex relative z-10">
      <div className="hidden md:block h-full shrink-0">
        {collapsed ? <Rail {...common} onExpand={() => setCollapsed(false)} /> : <Sidebar {...full} onCollapse={() => setCollapsed(true)} />}
      </div>
      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent ref={contentRef} overlayRef={overlayRef} side="left" className="md:hidden p-0 w-[86vw] max-w-[300px] bg-[#0F131D] border-white/10 [&>button.absolute]:hidden" data-testid="sidebar-drawer">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SheetDescription className="sr-only">Navigation and recent chats</SheetDescription>
          <Sidebar {...full} mobile onClose={() => setDrawer(false)} onPick={() => setDrawer(false)} />
        </SheetContent>
      </Sheet>
      <main className="flex-1 min-w-0 flex flex-col">
        <MobileBar onMenu={() => setDrawer(true)} onNew={() => setQuick(true)} />
        <Outlet context={outletCtx} />
        <PaywallDialog />
        <InstallCard />
      </main>
      <QuickActions open={quick} onOpenChange={setQuick} />
      <SearchChats open={search} onOpenChange={setSearch} chats={chats} />
    </div>
  );
}
